import { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Tooltip,
    Rating,
    Avatar,
    Button,
} from '@mui/material'
import {
    Visibility,
    Forum,
    Inbox as InboxIcon,
    MarkEmailRead as ReadIcon,
    Reply as ReplyIcon,
    HourglassEmpty as PendingIcon,
    Chat as ChatIcon,
    AccessTime,
    Person,
    Email,
    Star,
    QuestionAnswer,
    Download,
    Refresh,
    PictureAsPdf,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { chatSubmissionsApi } from '@api/chatSubmissions'
import { exportChatPDF } from '@utils/chatPdfExport'
import Loading from '@components/Common/Loading'
import { toast } from 'react-toastify'

const STATUS_COLORS = {
    new: 'primary',
    read: 'info',
    replied: 'success',
    pending_department: 'warning',
}

const ChatSubmissions = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { themeSettings } = useUIStore()
    const navigate = useNavigate()

    const isRTL = language === 'ar'

    // State
    const [submissions, setSubmissions] = useState([])
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [statsLoading, setStatsLoading] = useState(true)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(20)
    const [total, setTotal] = useState(0)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('')

    // Fetch submissions
    const fetchSubmissions = async () => {
        setLoading(true)
        try {
            const res = await chatSubmissionsApi.getAll({
                page: page + 1,
                perPage: rowsPerPage,
                search: search || undefined,
                status: statusFilter || undefined,
            })
            if (res.data) {
                setSubmissions(res.data.data || [])
                setTotal(res.data.total || 0)
            }
        } catch (error) {
            console.error('Error fetching chat submissions:', error)
            toast.error(isRTL ? 'خطأ في جلب المحادثات' : 'Error fetching chat submissions')
        } finally {
            setLoading(false)
        }
    }

    // Fetch stats
    const fetchStats = async () => {
        setStatsLoading(true)
        try {
            const res = await chatSubmissionsApi.getStats()
            if (res.data) {
                setStats(res.data)
            }
        } catch (error) {
            console.error('Error fetching stats:', error)
        } finally {
            setStatsLoading(false)
        }
    }

    useEffect(() => {
        fetchSubmissions()
        fetchStats()
    }, [page, rowsPerPage, search, statusFilter])

    // Bulk CSV export
    const handleExportCSV = () => {
        if (!submissions.length) return
        const headers = ['ID', 'Visitor', 'Email', 'Status', 'Duration', 'Agents', 'Messages', 'Rating', 'Created', 'Closed']
        const rows = submissions.map(s => {
            const d = s.data || {}
            return [
                s.submission_id,
                d.chat_visitor_name || '',
                d.chat_visitor_email || '',
                d.chat_status || '',
                d.chat_duration_formatted || '',
                d.chat_agents || '',
                d.chat_total_messages || 0,
                d.chat_rating || 0,
                d.chat_created_at || '',
                d.chat_closed_at || '',
            ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
        })
        const csv = '\uFEFF' + [headers.join(','), ...rows].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `chat_submissions_${new Date().toISOString().split('T')[0]}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    if (loading && !submissions.length) {
        return <Loading />
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <QuestionAnswer sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight={700}>
                            {isRTL ? 'المحادثات المحفوظة' : 'Chat Submissions'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isRTL ? 'عرض وإدارة المحادثات المحفوظة من الدردشة المباشرة' : 'View and manage saved live chat conversations'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExportCSV}
                        disabled={!submissions.length}
                        size="small"
                    >
                        {isRTL ? 'تصدير CSV' : 'Export CSV'}
                    </Button>
                    <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
                        <IconButton onClick={() => { fetchSubmissions(); fetchStats() }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Stats Cards */}
            {!statsLoading && stats && (() => {
                const statCards = [
                    {
                        key: 'total',
                        label: isRTL ? 'الإجمالي' : 'Total',
                        value: stats.total || 0,
                        icon: <Forum />,
                        color: '#1a3a6b',
                        bgColor: 'rgba(102, 126, 234, 0.1)',
                        filter: '',
                    },
                    {
                        key: 'new',
                        label: isRTL ? 'جديد' : 'New',
                        value: stats.new_count || 0,
                        icon: <InboxIcon />,
                        color: '#3b82f6',
                        bgColor: 'rgba(59, 130, 246, 0.1)',
                        filter: 'new',
                    },
                    {
                        key: 'read',
                        label: isRTL ? 'مقروء' : 'Read',
                        value: stats.read_count || 0,
                        icon: <ReadIcon />,
                        color: '#10b981',
                        bgColor: 'rgba(16, 185, 129, 0.1)',
                        filter: 'read',
                    },
                    {
                        key: 'replied',
                        label: isRTL ? 'تم الرد' : 'Replied',
                        value: stats.replied_count || 0,
                        icon: <ReplyIcon />,
                        color: '#8b5cf6',
                        bgColor: 'rgba(139, 92, 246, 0.1)',
                        filter: 'replied',
                    },
                    {
                        key: 'pending_department',
                        label: isRTL ? 'معلّق للقسم' : 'Pending Dept.',
                        value: stats.pending_department_count || 0,
                        icon: <PendingIcon />,
                        color: '#f59e0b',
                        bgColor: 'rgba(245, 158, 11, 0.1)',
                        filter: 'pending_department',
                    },
                ]
                return (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {statCards.map((card) => {
                            const isActive = statusFilter === card.filter
                            return (
                                <Grid item xs={12} sm={6} md key={card.key}>
                                    <Paper
                                        onClick={() => {
                                            setStatusFilter(card.filter)
                                            setPage(0)
                                        }}
                                        sx={{
                                            p: 2,
                                            cursor: 'pointer',
                                            border: isActive ? `2px solid ${card.color}` : '2px solid transparent',
                                            borderRadius: 2,
                                            transition: 'all 0.2s ease',
                                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                                        }}
                                        elevation={isActive ? 4 : 1}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: card.bgColor, color: card.color, width: 44, height: 44 }}>
                                                {card.icon}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h5" fontWeight={700}>{card.value}</Typography>
                                                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            )
                        })}
                    </Grid>
                )
            })()}

            {/* Search and Filters */}
            <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }} elevation={1}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder={isRTL ? 'بحث بالاسم أو البريد أو المعرف...' : 'Search by name, email or ID...'}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            setPage(0)
                        }}
                        sx={{ minWidth: 280 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>{isRTL ? 'الحالة' : 'Status'}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={isRTL ? 'الحالة' : 'Status'}
                            onChange={(e) => {
                                setStatusFilter(e.target.value)
                                setPage(0)
                            }}
                        >
                            <MenuItem value="">{isRTL ? 'الكل' : 'All'}</MenuItem>
                            <MenuItem value="new">{isRTL ? 'جديد' : 'New'}</MenuItem>
                            <MenuItem value="read">{isRTL ? 'مقروء' : 'Read'}</MenuItem>
                            <MenuItem value="replied">{isRTL ? 'تم الرد' : 'Replied'}</MenuItem>
                            <MenuItem value="pending_department">{isRTL ? 'معلّق للقسم' : 'Pending Dept.'}</MenuItem>
                        </Select>
                    </FormControl>
                    {(search || statusFilter) && (
                        <Button
                            size="small"
                            onClick={() => { setSearch(''); setStatusFilter(''); setPage(0) }}
                        >
                            {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2 }} elevation={1}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'المعرف' : 'ID'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'الزائر' : 'Visitor'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'الرسائل' : 'Messages'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'المدة' : 'Duration'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'التقييم' : 'Rating'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'التاريخ' : 'Date'}</TableCell>
                            <TableCell sx={{ fontWeight: 700 }} align="center">{isRTL ? 'الإجراءات' : 'Actions'}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {submissions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <Forum sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="text.secondary">
                                        {isRTL ? 'لا توجد محادثات محفوظة' : 'No saved chat conversations found'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            submissions.map((sub) => {
                                const d = sub.data || {}
                                return (
                                    <TableRow
                                        key={sub.id}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                        onClick={() => navigate(`/submissions/${sub.id}`)}
                                    >
                                        {/* ID */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <ChatIcon fontSize="small" color="primary" />
                                                <Typography variant="body2" fontWeight={600}>
                                                    {sub.submission_id}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* Visitor */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: 14 }}>
                                                    {(d.chat_visitor_name || '?')[0]?.toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 150 }}>
                                                        {d.chat_visitor_name || (isRTL ? 'زائر' : 'Visitor')}
                                                    </Typography>
                                                    {d.chat_visitor_email && (
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150, display: 'block' }}>
                                                            {d.chat_visitor_email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>
                                            <Chip
                                                label={sub.status?.replace('_', ' ')}
                                                color={STATUS_COLORS[sub.status] || 'default'}
                                                size="small"
                                                sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: 11 }}
                                            />
                                        </TableCell>

                                        {/* Messages */}
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {d.chat_total_messages || 0}
                                            </Typography>
                                        </TableCell>

                                        {/* Duration */}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {d.chat_duration_formatted || '—'}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* Rating */}
                                        <TableCell>
                                            {d.chat_rating > 0 ? (
                                                <Rating value={d.chat_rating} readOnly size="small" max={5} />
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>

                                        {/* Date */}
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {sub.created_at ? new Date(sub.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
                                                    year: 'numeric', month: 'short', day: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                }) : '—'}
                                            </Typography>
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title={isRTL ? 'عرض التفاصيل' : 'View Details'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            navigate(`/submissions/${sub.id}`)
                                                        }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isRTL ? 'تصدير PDF عربي' : 'Export PDF Arabic'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            exportChatPDF(sub, 'ar', themeSettings)
                                                        }}
                                                    >
                                                        <PictureAsPdf fontSize="small" sx={{ color: '#e53e3e' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isRTL ? 'تصدير PDF إنجليزي' : 'Export PDF English'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            exportChatPDF(sub, 'en', themeSettings)
                                                        }}
                                                    >
                                                        <PictureAsPdf fontSize="small" sx={{ color: '#3182ce' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10))
                        setPage(0)
                    }}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage={isRTL ? 'صفوف لكل صفحة:' : 'Rows per page:'}
                />
            </TableContainer>
        </Box>
    )
}

export default ChatSubmissions
